// SEO component for dynamic meta tags
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { stripHtml } from '../utils/sanitizeHtml';
import { fetchSeoOverrides } from '../utils/seoOverrides';
import { fetchStoreSettings } from '../utils/storeSettings';

const SITE_URL = 'https://www.justkeepshrimping.com';
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const DEFAULT_IMAGE = `${API_BASE}/api/logo/no_text`;

export const SEO = ({ 
  title = 'Just Keep Shrimping',
  description = 'Buy freshwater aquarium shrimp in Ireland. DAFM-registered Neocaridina breeder offering pet shrimp delivered nationwide — Cherry Shrimp, Bloody Mary, Blue Dream & more.',
  image = DEFAULT_IMAGE,
  url = SITE_URL,
  type = 'website',
  product = null,
  products = null,
  article = null,
  faq = null,
  noindex = false,
  pageKey = null,       // key into admin per-page SEO overrides (e.g. 'home')
  exactTitle = false    // when true, use `title` verbatim without the site suffix
}) => {
  const [ov, setOv] = useState(null);
  useEffect(() => {
    let mounted = true;
    if (pageKey) fetchSeoOverrides().then((d) => { if (mounted) setOv(d); });
    return () => { mounted = false; };
  }, [pageKey]);

  // Live store settings (shipping rates + return policy) for product structured data.
  const needsStore = !!(product || (products && products.length));
  const [store, setStore] = useState(null);
  useEffect(() => {
    let mounted = true;
    if (needsStore) fetchStoreSettings().then((d) => { if (mounted) setStore(d); });
    return () => { mounted = false; };
  }, [needsStore]);

  const pageOverride = (pageKey && ov?.pages?.[pageKey]) || {};
  const globalSuffix = (ov?.global?.title_suffix !== undefined && ov?.global?.title_suffix !== null)
    ? ov.global.title_suffix
    : ' | Just Keep Shrimping';

  // Admin-set titles are used exactly as typed (owner controls the search headline).
  let resolvedTitle = title;
  let isExact = exactTitle;
  if (pageOverride.title) { resolvedTitle = pageOverride.title; isExact = true; }

  const fullTitle = isExact
    ? resolvedTitle
    : (resolvedTitle === 'Just Keep Shrimping' ? resolvedTitle : `${resolvedTitle}${globalSuffix}`);

  const effDescription = pageOverride.description || description;
  const effImage = pageOverride.image || ov?.global?.default_share_image || image;
  const effUrl = pageOverride.canonical || url;
  const effNoindex = (pageOverride.noindex ?? noindex);

  const buildProductSchema = (p) => {
    const s = store || {};
    const shipVal = Number(s.shipping_rate_under_100 ?? 20).toFixed(2);
    const cat = s.return_policy_category || 'finite';
    const catUrl = cat === 'none'
      ? 'MerchantReturnNotPermitted'
      : cat === 'unlimited'
        ? 'MerchantReturnUnlimitedWindow'
        : 'MerchantReturnFiniteReturnWindow';
    const returnPolicy = {
      "@type": "MerchantReturnPolicy",
      "applicableCountry": "IE",
      "returnPolicyCategory": `https://schema.org/${catUrl}`
    };
    if (cat !== 'none') {
      if (cat === 'finite') returnPolicy.merchantReturnDays = Number(s.return_policy_days ?? 1);
      returnPolicy.returnMethod = "https://schema.org/ReturnByMail";
      if (s.return_policy_free !== false) returnPolicy.returnFees = "https://schema.org/FreeReturn";
    }
    return {
      "@type": "Product",
      "name": p.name,
      "description": stripHtml(p.description) || `${p.name} - Home-bred Neocaridina shrimp from Just Keep Shrimping`,
      "image": p.image || effImage,
      "brand": { "@type": "Brand", "name": "Just Keep Shrimping" },
      "offers": {
        "@type": "Offer",
        "url": p.url || effUrl,
        "priceCurrency": "EUR",
        "price": p.price,
        "availability": p.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "seller": { "@type": "Organization", "name": "Just Keep Shrimping" },
        "shippingDetails": {
          "@type": "OfferShippingDetails",
          "shippingRate": {
            "@type": "MonetaryAmount",
            "value": shipVal,
            "currency": "EUR"
          },
          "shippingDestination": {
            "@type": "DefinedRegion",
            "addressCountry": "IE"
          },
          "deliveryTime": {
            "@type": "ShippingDeliveryTime",
            "handlingTime": {
              "@type": "QuantitativeValue",
              "minValue": 0,
              "maxValue": 2,
              "unitCode": "DAY"
            },
            "transitTime": {
              "@type": "QuantitativeValue",
              "minValue": 1,
              "maxValue": 3,
              "unitCode": "DAY"
            }
          }
        },
        "hasMerchantReturnPolicy": returnPolicy
      },
      "category": p.category || "Aquarium Shrimp",
      ...(p.rating && p.rating.count > 0 ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": p.rating.average,
          "reviewCount": p.rating.count,
          "bestRating": 5,
          "worstRating": 1
        },
        ...(p.rating.reviews && p.rating.reviews.length ? {
          "review": p.rating.reviews.slice(0, 5).map((rv) => ({
            "@type": "Review",
            "author": { "@type": "Person", "name": rv.display_name || "Verified buyer" },
            "reviewRating": { "@type": "Rating", "ratingValue": rv.rating, "bestRating": 5, "worstRating": 1 },
            ...(rv.comment ? { "reviewBody": rv.comment } : {}),
            ...(rv.created_at ? { "datePublished": rv.created_at } : {})
          }))
        } : {})
      } : {})
    };
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={effDescription} />
      <link rel="canonical" href={effUrl} />
      {effNoindex && <meta name="robots" content="noindex, nofollow" />}
      
      <meta property="og:type" content={type} />
      <meta property="og:url" content={effUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={effDescription} />
      <meta property="og:image" content={effImage} />
      <meta property="og:site_name" content="Just Keep Shrimping" />
      <meta property="og:locale" content="en_IE" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={effDescription} />
      <meta name="twitter:image" content={effImage} />
      
      {/* Single Product schema */}
      {product && (
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", ...buildProductSchema(product) })}
        </script>
      )}
      
      {/* Product listing (ItemList) schema for catalog pages */}
      {products && products.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Neocaridina Shrimp for Sale",
            "description": "Browse home-bred, DAFM-registered Neocaridina shrimp colonies available for purchase in Ireland",
            "numberOfItems": products.length,
            "itemListElement": products.map((p, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "item": buildProductSchema(p)
            }))
          })}
        </script>
      )}
      
      {/* Blog article schema */}
      {article && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": article.title,
            "description": article.excerpt || effDescription,
            "image": article.image || effImage,
            "datePublished": article.publishedAt,
            "dateModified": article.updatedAt || article.publishedAt,
            "author": { "@type": "Person", "name": article.author || "Sanjin Blazinic" },
            "publisher": {
              "@type": "Organization",
              "name": "Just Keep Shrimping",
              "logo": { "@type": "ImageObject", "url": DEFAULT_IMAGE }
            },
            "mainEntityOfPage": { "@type": "WebPage", "@id": effUrl }
          })}
        </script>
      )}
      
      {/* FAQ schema for rich snippets */}
      {faq && faq.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faq.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
              }
            }))
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

// Private / transactional pages (login, checkout, account…) must stay crawlable
// (so Google can read the directive) but carry noindex instead of being blocked
// in robots.txt — otherwise Search Console reports "Blocked by robots.txt".
export const NoIndex = ({ title = 'Just Keep Shrimping' }) => {
  const { pathname } = useLocation();
  return <SEO title={title} url={`${SITE_URL}${pathname}`} noindex />;
};
